/**
 * DungeonData.ts - Dungeon floor layouts and configuration
 * Contains tile maps, stairs, NPCs, and bosses for all dungeons
 */

export enum DungeonTileType {
  FLOOR = 0,
  WALL = 1,
  WALL_CORNER_TL = 2,
  WALL_CORNER_TR = 3,
  WALL_CORNER_BL = 4,
  WALL_CORNER_BR = 5,
  DOOR = 6,
  STAIRS_UP = 7,
  STAIRS_DOWN = 8,
  PILLAR = 9,
  WATER = 10,
  LAVA = 11,
  CHASM = 12,
  ALTAR = 13,
  THRONE = 14,
  TREASURE_CLOSED = 15,
  TREASURE_OPEN = 16,
}

export interface DungeonStairs {
  x: number;
  y: number;
  direction: 'up' | 'down';
  targetFloor: number;
  targetX?: number;
  targetY?: number;
}

export interface DungeonNPC {
  x: number;
  y: number;
  id: string;
  name: string;
  dialogue?: string[];
  isBoss?: boolean;
  enemyId?: number; // Enemy ID from EnemyData.sol
}

export interface DungeonFloor {
  width: number;
  height: number;
  tiles: DungeonTileType[][];
  stairs: DungeonStairs[];
  npcs?: DungeonNPC[];
  encounterRate: number; // Base rate 0-100
  encounterZone: string;
  music?: string;
  ambientColor?: string; // Tint for atmosphere
}

export interface Dungeon {
  id: string;
  name: string;
  description: string;
  floors: DungeonFloor[];
  entryFloor: number;
  entryX: number;
  entryY: number;
  requiredItem?: string;
  bossFloor?: number;
  bossId?: number;
}

// Helper function to create rectangular rooms
function createEmptyFloor(width: number, height: number): DungeonTileType[][] {
  return Array(height)
    .fill(null)
    .map(() => Array(width).fill(DungeonTileType.WALL));
}

// Helper function to safely set a tile
function setTile(tiles: DungeonTileType[][], y: number, x: number, type: DungeonTileType): void {
  const row = tiles[y];
  if (row) {
    row[x] = type;
  }
}

function fillRoom(
  tiles: DungeonTileType[][],
  x: number,
  y: number,
  width: number,
  height: number,
  fillType: DungeonTileType = DungeonTileType.FLOOR
): void {
  for (let dy = 0; dy < height; dy++) {
    for (let dx = 0; dx < width; dx++) {
      const ty = y + dy;
      const tx = x + dx;
      if (ty >= 0 && ty < tiles.length && tiles[0] && tx >= 0 && tx < tiles[0].length) {
        setTile(tiles, ty, tx, fillType);
      }
    }
  }
}

function createCorridor(
  tiles: DungeonTileType[][],
  x1: number,
  y1: number,
  x2: number,
  y2: number
): void {
  // Horizontal then vertical
  let x = x1;
  let y = y1;

  while (x !== x2) {
    setTile(tiles, y, x, DungeonTileType.FLOOR);
    x += x < x2 ? 1 : -1;
  }

  while (y !== y2) {
    setTile(tiles, y, x, DungeonTileType.FLOOR);
    y += y < y2 ? 1 : -1;
  }
}

// ========== TEMPLE OF FIENDS (Tutorial Dungeon) ==========

function createTempleOfFiendsFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Main entrance hall
  fillRoom(tiles, 8, 1, 4, 6);

  // Connecting corridor
  createCorridor(tiles, 10, 6, 10, 10);

  // Large central room
  fillRoom(tiles, 4, 10, 12, 8);

  // Side rooms
  fillRoom(tiles, 1, 3, 4, 3); // Left treasure room
  createCorridor(tiles, 4, 4, 8, 4);

  fillRoom(tiles, 15, 3, 4, 3); // Right treasure room
  createCorridor(tiles, 12, 4, 15, 4);

  return tiles;
}

function createTempleOfFiendsFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Maze-like layout
  fillRoom(tiles, 8, 16, 4, 3); // Entry from stairs

  // Twisting corridors
  createCorridor(tiles, 10, 16, 10, 12);
  createCorridor(tiles, 10, 12, 6, 12);
  createCorridor(tiles, 6, 12, 6, 8);
  createCorridor(tiles, 6, 8, 14, 8);
  createCorridor(tiles, 14, 8, 14, 4);

  // Small chambers
  fillRoom(tiles, 3, 10, 3, 4);
  fillRoom(tiles, 12, 4, 4, 3);

  return tiles;
}

function createTempleOfFiendsFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 16, 4, 3);
  createCorridor(tiles, 10, 16, 10, 10);

  // Boss chamber - large circular-ish room
  fillRoom(tiles, 6, 6, 8, 8);
  setTile(tiles, 10, 10, DungeonTileType.THRONE); // Garland's throne

  return tiles;
}

// ========== MARSH CAVE ==========

function createMarshCaveFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Winding cave entrance
  fillRoom(tiles, 8, 17, 4, 2);
  createCorridor(tiles, 10, 17, 10, 13);
  fillRoom(tiles, 6, 10, 8, 6);

  // Water pools (poison/swamp)
  setTile(tiles, 12, 8, DungeonTileType.WATER);
  setTile(tiles, 12, 9, DungeonTileType.WATER);
  setTile(tiles, 13, 8, DungeonTileType.WATER);

  // Branch paths
  createCorridor(tiles, 6, 13, 3, 13);
  fillRoom(tiles, 1, 11, 3, 4); // Treasure chamber

  createCorridor(tiles, 14, 13, 17, 13);
  fillRoom(tiles, 16, 10, 3, 6); // Another chamber

  return tiles;
}

function createMarshCaveFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Complex cave network
  fillRoom(tiles, 8, 1, 4, 3); // Entry from stairs
  createCorridor(tiles, 10, 3, 10, 8);

  // Central hub
  fillRoom(tiles, 5, 8, 10, 8);

  // Water hazards
  setTile(tiles, 10, 7, DungeonTileType.WATER);
  setTile(tiles, 11, 7, DungeonTileType.WATER);
  setTile(tiles, 10, 8, DungeonTileType.WATER);

  // Multiple branching paths
  createCorridor(tiles, 5, 12, 2, 12);
  fillRoom(tiles, 1, 10, 3, 4);

  createCorridor(tiles, 15, 12, 18, 12);
  fillRoom(tiles, 16, 13, 3, 4);

  return tiles;
}

function createMarshCaveFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Final chamber
  fillRoom(tiles, 8, 16, 4, 3); // Entry
  createCorridor(tiles, 10, 16, 10, 12);

  // Large treasure room
  fillRoom(tiles, 4, 8, 12, 8);

  // Crown chamber (center)
  fillRoom(tiles, 8, 10, 4, 4);
  setTile(tiles, 12, 10, DungeonTileType.ALTAR); // Crown pedestal

  // Side treasure rooms
  fillRoom(tiles, 1, 13, 3, 4);
  createCorridor(tiles, 4, 15, 1, 15);

  return tiles;
}

// ========== EARTH CAVE (5 floors + boss) ==========

function createEarthCaveFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Large entrance chamber
  fillRoom(tiles, 9, 18, 4, 3);
  fillRoom(tiles, 7, 14, 8, 6);

  // Branching paths
  createCorridor(tiles, 11, 14, 11, 10);
  fillRoom(tiles, 8, 8, 6, 4);

  // Side chambers
  createCorridor(tiles, 8, 10, 3, 10);
  fillRoom(tiles, 1, 8, 4, 4);

  createCorridor(tiles, 14, 10, 18, 10);
  fillRoom(tiles, 17, 8, 4, 4);

  return tiles;
}

function createEarthCaveFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry from above
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 8);

  // Large central cavern
  fillRoom(tiles, 5, 8, 12, 10);

  // Pillars in cavern
  setTile(tiles, 10, 8, DungeonTileType.PILLAR);
  setTile(tiles, 10, 15, DungeonTileType.PILLAR);
  setTile(tiles, 15, 8, DungeonTileType.PILLAR);
  setTile(tiles, 15, 15, DungeonTileType.PILLAR);

  // Exit paths
  createCorridor(tiles, 11, 18, 11, 20);

  return tiles;
}

function createEarthCaveFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Maze-like tunnels
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 16);
  createCorridor(tiles, 11, 16, 6, 16);
  createCorridor(tiles, 6, 16, 6, 12);
  createCorridor(tiles, 6, 12, 15, 12);
  createCorridor(tiles, 15, 12, 15, 8);

  // Small rooms
  fillRoom(tiles, 3, 14, 3, 3);
  fillRoom(tiles, 13, 6, 4, 3);

  // Treasure chambers
  fillRoom(tiles, 1, 3, 4, 4);
  createCorridor(tiles, 5, 5, 6, 5);

  fillRoom(tiles, 17, 14, 4, 4);
  createCorridor(tiles, 15, 16, 17, 16);

  return tiles;
}

function createEarthCaveFloor4(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 8);

  // Multiple chambers
  fillRoom(tiles, 6, 8, 10, 6);
  fillRoom(tiles, 3, 15, 6, 5);
  fillRoom(tiles, 13, 15, 6, 5);

  // Connecting corridors
  createCorridor(tiles, 11, 14, 11, 18);
  createCorridor(tiles, 6, 18, 16, 18);

  return tiles;
}

function createEarthCaveFloor5(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entrance
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 14);

  // Lich's chamber - large and imposing
  fillRoom(tiles, 5, 8, 12, 10);

  // Boss position (center)
  setTile(tiles, 13, 11, DungeonTileType.ALTAR); // Lich's throne

  return tiles;
}

// ========== GURGU VOLCANO (5 floors + boss) ==========

function createVolcanoFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry chamber
  fillRoom(tiles, 9, 18, 4, 3);
  createCorridor(tiles, 11, 18, 11, 13);

  // Large cavern with lava
  fillRoom(tiles, 5, 10, 12, 8);

  // Lava pools
  setTile(tiles, 12, 7, DungeonTileType.LAVA);
  setTile(tiles, 12, 8, DungeonTileType.LAVA);
  setTile(tiles, 13, 7, DungeonTileType.LAVA);
  setTile(tiles, 13, 8, DungeonTileType.LAVA);

  // Side paths
  createCorridor(tiles, 5, 14, 2, 14);
  fillRoom(tiles, 1, 12, 3, 4);

  createCorridor(tiles, 17, 14, 20, 14);
  fillRoom(tiles, 18, 12, 3, 4);

  return tiles;
}

function createVolcanoFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 6);

  // Winding paths around lava
  fillRoom(tiles, 6, 6, 10, 12);

  // Large lava lake in center
  fillRoom(tiles, 9, 10, 4, 6, DungeonTileType.LAVA);

  // Narrow passages
  createCorridor(tiles, 11, 18, 11, 20);

  return tiles;
}

function createVolcanoFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry from below
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 15);

  // Complex lava-filled chambers
  fillRoom(tiles, 4, 12, 14, 8);

  // Lava hazards
  setTile(tiles, 14, 6, DungeonTileType.LAVA);
  setTile(tiles, 14, 7, DungeonTileType.LAVA);
  setTile(tiles, 15, 6, DungeonTileType.LAVA);
  setTile(tiles, 15, 7, DungeonTileType.LAVA);
  setTile(tiles, 16, 13, DungeonTileType.LAVA);
  setTile(tiles, 16, 14, DungeonTileType.LAVA);

  // Treasure chambers
  fillRoom(tiles, 1, 6, 3, 4);
  createCorridor(tiles, 4, 8, 1, 8);

  fillRoom(tiles, 18, 6, 3, 4);
  createCorridor(tiles, 18, 8, 15, 8);

  return tiles;
}

function createVolcanoFloor4(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 7);

  // Large chamber
  fillRoom(tiles, 4, 7, 14, 12);

  // Scattered lava pools
  setTile(tiles, 10, 8, DungeonTileType.LAVA);
  setTile(tiles, 10, 13, DungeonTileType.LAVA);
  setTile(tiles, 15, 8, DungeonTileType.LAVA);
  setTile(tiles, 15, 13, DungeonTileType.LAVA);

  // Path to boss
  createCorridor(tiles, 11, 19, 11, 20);

  return tiles;
}

function createVolcanoFloor5(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 14);

  // Kary's chamber - ring of lava
  fillRoom(tiles, 5, 8, 12, 10);

  // Lava ring around center
  for (let x = 6; x <= 15; x++) {
    setTile(tiles, 9, x, DungeonTileType.LAVA);
    setTile(tiles, 16, x, DungeonTileType.LAVA);
  }
  for (let y = 10; y <= 15; y++) {
    setTile(tiles, y, 6, DungeonTileType.LAVA);
    setTile(tiles, y, 15, DungeonTileType.LAVA);
  }

  // Boss position (center)
  setTile(tiles, 13, 11, DungeonTileType.THRONE);

  return tiles;
}

// ========== ICE CAVE (3 floors) ==========

function createIceCaveFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 17, 4, 2);
  createCorridor(tiles, 10, 17, 10, 12);

  // Main cave
  fillRoom(tiles, 5, 10, 10, 6);

  // Frozen water hazards
  setTile(tiles, 12, 7, DungeonTileType.WATER);
  setTile(tiles, 13, 7, DungeonTileType.WATER);
  setTile(tiles, 12, 12, DungeonTileType.WATER);
  setTile(tiles, 13, 12, DungeonTileType.WATER);

  // Side chambers
  createCorridor(tiles, 5, 13, 2, 13);
  fillRoom(tiles, 1, 11, 3, 4);

  createCorridor(tiles, 15, 13, 18, 13);
  fillRoom(tiles, 16, 11, 3, 4);

  return tiles;
}

function createIceCaveFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 1, 4, 3);
  createCorridor(tiles, 10, 3, 10, 7);

  // Large frozen chamber
  fillRoom(tiles, 4, 7, 12, 10);

  // Ice pillars
  setTile(tiles, 9, 6, DungeonTileType.PILLAR);
  setTile(tiles, 9, 13, DungeonTileType.PILLAR);
  setTile(tiles, 14, 6, DungeonTileType.PILLAR);
  setTile(tiles, 14, 13, DungeonTileType.PILLAR);

  // Slippery paths
  createCorridor(tiles, 10, 17, 10, 19);

  return tiles;
}

function createIceCaveFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 18, 4, 2);
  createCorridor(tiles, 10, 18, 10, 13);

  // Treasure chamber
  fillRoom(tiles, 6, 8, 8, 8);

  // Levistone pedestal
  setTile(tiles, 12, 10, DungeonTileType.ALTAR);

  // Hidden passages
  createCorridor(tiles, 6, 10, 2, 10);
  fillRoom(tiles, 1, 8, 3, 4);

  createCorridor(tiles, 14, 10, 18, 10);
  fillRoom(tiles, 16, 3, 3, 4);

  return tiles;
}

// ========== SEA SHRINE (5 floors + boss) ==========

function createSeaShrineFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Underwater entrance
  fillRoom(tiles, 9, 18, 4, 3);
  fillRoom(tiles, 7, 13, 8, 7, DungeonTileType.FLOOR);

  // Water-filled chambers
  setTile(tiles, 15, 9, DungeonTileType.WATER);
  setTile(tiles, 15, 10, DungeonTileType.WATER);
  setTile(tiles, 15, 11, DungeonTileType.WATER);
  setTile(tiles, 16, 9, DungeonTileType.WATER);
  setTile(tiles, 16, 10, DungeonTileType.WATER);
  setTile(tiles, 16, 11, DungeonTileType.WATER);

  // Corridors
  createCorridor(tiles, 11, 13, 11, 8);
  fillRoom(tiles, 8, 6, 6, 4);

  return tiles;
}

function createSeaShrineFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 7);

  // Large water chamber
  fillRoom(tiles, 5, 7, 12, 10);

  // Water pools
  fillRoom(tiles, 8, 10, 6, 4, DungeonTileType.WATER);

  // Narrow walkways
  setTile(tiles, 12, 10, DungeonTileType.FLOOR);
  setTile(tiles, 12, 11, DungeonTileType.FLOOR);
  setTile(tiles, 12, 12, DungeonTileType.FLOOR);

  createCorridor(tiles, 11, 17, 11, 20);

  return tiles;
}

function createSeaShrineFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 14);

  // Complex water maze
  fillRoom(tiles, 4, 10, 14, 8);

  // Water hazards
  setTile(tiles, 12, 5, DungeonTileType.WATER);
  setTile(tiles, 13, 5, DungeonTileType.WATER);
  setTile(tiles, 14, 5, DungeonTileType.WATER);
  setTile(tiles, 12, 16, DungeonTileType.WATER);
  setTile(tiles, 13, 16, DungeonTileType.WATER);
  setTile(tiles, 14, 16, DungeonTileType.WATER);

  // Treasure rooms
  createCorridor(tiles, 4, 14, 1, 14);
  fillRoom(tiles, 1, 12, 3, 4);

  createCorridor(tiles, 18, 14, 20, 14);
  fillRoom(tiles, 18, 12, 3, 4);

  return tiles;
}

function createSeaShrineFloor4(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 7);

  // Large chamber
  fillRoom(tiles, 4, 7, 14, 12);

  // Pillars
  setTile(tiles, 9, 6, DungeonTileType.PILLAR);
  setTile(tiles, 9, 15, DungeonTileType.PILLAR);
  setTile(tiles, 16, 6, DungeonTileType.PILLAR);
  setTile(tiles, 16, 15, DungeonTileType.PILLAR);

  // Water features
  setTile(tiles, 11, 8, DungeonTileType.WATER);
  setTile(tiles, 11, 13, DungeonTileType.WATER);
  setTile(tiles, 14, 8, DungeonTileType.WATER);
  setTile(tiles, 14, 13, DungeonTileType.WATER);

  createCorridor(tiles, 11, 19, 11, 20);

  return tiles;
}

function createSeaShrineFloor5(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 14);

  // Kraken's chamber - circular pool
  fillRoom(tiles, 5, 8, 12, 10);

  // Water pool (Kraken rises from here)
  fillRoom(tiles, 8, 11, 6, 4, DungeonTileType.WATER);

  // Boss position
  setTile(tiles, 13, 11, DungeonTileType.ALTAR);

  return tiles;
}

// ========== MIRAGE TOWER (3 floors) ==========

function createMirageTowerFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 17, 4, 2);
  createCorridor(tiles, 10, 17, 10, 13);

  // Geometric chamber
  fillRoom(tiles, 6, 10, 8, 6);

  // Pillars
  setTile(tiles, 11, 7, DungeonTileType.PILLAR);
  setTile(tiles, 11, 14, DungeonTileType.PILLAR);
  setTile(tiles, 14, 7, DungeonTileType.PILLAR);
  setTile(tiles, 14, 14, DungeonTileType.PILLAR);

  // Side chambers
  createCorridor(tiles, 6, 13, 3, 13);
  fillRoom(tiles, 1, 11, 3, 4);

  createCorridor(tiles, 14, 13, 17, 13);
  fillRoom(tiles, 16, 11, 3, 4);

  return tiles;
}

function createMirageTowerFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 1, 4, 3);
  createCorridor(tiles, 10, 3, 10, 6);

  // Large hall
  fillRoom(tiles, 4, 6, 12, 10);

  // Chasms (illusions)
  setTile(tiles, 8, 6, DungeonTileType.CHASM);
  setTile(tiles, 8, 13, DungeonTileType.CHASM);
  setTile(tiles, 13, 6, DungeonTileType.CHASM);
  setTile(tiles, 13, 13, DungeonTileType.CHASM);

  createCorridor(tiles, 10, 16, 10, 18);

  return tiles;
}

function createMirageTowerFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry
  fillRoom(tiles, 8, 18, 4, 2);
  createCorridor(tiles, 10, 18, 10, 13);

  // Final chamber - leads to Sky Castle
  fillRoom(tiles, 6, 8, 8, 8);

  // Adamant pedestal
  setTile(tiles, 12, 10, DungeonTileType.ALTAR);

  // Warp to Sky Castle
  setTile(tiles, 10, 10, DungeonTileType.STAIRS_UP); // Special warp tile

  return tiles;
}

// ========== SKY CASTLE (5 floors + boss) ==========

function createSkyCastleFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry from warp
  fillRoom(tiles, 9, 18, 4, 3);
  createCorridor(tiles, 11, 18, 11, 14);

  // Grand hall
  fillRoom(tiles, 5, 10, 12, 8);

  // Pillars
  setTile(tiles, 11, 7, DungeonTileType.PILLAR);
  setTile(tiles, 11, 14, DungeonTileType.PILLAR);
  setTile(tiles, 15, 7, DungeonTileType.PILLAR);
  setTile(tiles, 15, 14, DungeonTileType.PILLAR);

  // Side chambers
  fillRoom(tiles, 1, 12, 4, 4);
  createCorridor(tiles, 5, 14, 1, 14);

  fillRoom(tiles, 17, 12, 4, 4);
  createCorridor(tiles, 17, 14, 13, 14);

  return tiles;
}

function createSkyCastleFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 7);

  // Multiple chambers
  fillRoom(tiles, 6, 7, 10, 8);
  fillRoom(tiles, 3, 16, 6, 5);
  fillRoom(tiles, 13, 16, 6, 5);

  // Connecting paths
  createCorridor(tiles, 11, 15, 11, 19);
  createCorridor(tiles, 6, 19, 16, 19);

  return tiles;
}

function createSkyCastleFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 15);

  // Complex layout
  fillRoom(tiles, 5, 11, 12, 8);

  // Chasms
  setTile(tiles, 13, 7, DungeonTileType.CHASM);
  setTile(tiles, 13, 15, DungeonTileType.CHASM);

  // Treasure chambers
  fillRoom(tiles, 1, 13, 4, 4);
  createCorridor(tiles, 5, 15, 1, 15);

  fillRoom(tiles, 17, 5, 4, 4);
  createCorridor(tiles, 17, 7, 13, 7);

  return tiles;
}

function createSkyCastleFloor4(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 1, 4, 3);
  createCorridor(tiles, 11, 3, 11, 7);

  // Large chamber
  fillRoom(tiles, 4, 7, 14, 12);

  // Pillars
  setTile(tiles, 9, 6, DungeonTileType.PILLAR);
  setTile(tiles, 9, 15, DungeonTileType.PILLAR);
  setTile(tiles, 16, 6, DungeonTileType.PILLAR);
  setTile(tiles, 16, 15, DungeonTileType.PILLAR);

  createCorridor(tiles, 11, 19, 11, 20);

  return tiles;
}

function createSkyCastleFloor5(): DungeonTileType[][] {
  const tiles = createEmptyFloor(22, 22);

  // Entry
  fillRoom(tiles, 9, 19, 4, 2);
  createCorridor(tiles, 11, 19, 11, 14);

  // Tiamat's throne room
  fillRoom(tiles, 5, 8, 12, 10);

  // Pillars forming a pattern
  setTile(tiles, 10, 8, DungeonTileType.PILLAR);
  setTile(tiles, 10, 13, DungeonTileType.PILLAR);
  setTile(tiles, 15, 8, DungeonTileType.PILLAR);
  setTile(tiles, 15, 13, DungeonTileType.PILLAR);

  // Boss throne
  setTile(tiles, 13, 11, DungeonTileType.THRONE);

  return tiles;
}

// ========== TEMPLE OF FIENDS REVISITED (10 floors + Chaos) ==========

function createTemplePastFloor(floorNum: number): DungeonTileType[][] {
  const tiles = createEmptyFloor(24, 24);

  // Entry point
  fillRoom(tiles, 10, 20, 4, 3);
  createCorridor(tiles, 12, 20, 12, 16);

  // Create increasingly complex maze
  switch (floorNum) {
    case 1:
    case 2:
      // Simple layout
      fillRoom(tiles, 8, 12, 8, 8);
      createCorridor(tiles, 12, 12, 12, 8);
      fillRoom(tiles, 10, 6, 4, 4);
      break;

    case 3:
    case 4:
      // Medium complexity
      fillRoom(tiles, 6, 14, 12, 6);
      createCorridor(tiles, 12, 14, 12, 10);
      createCorridor(tiles, 12, 10, 8, 10);
      fillRoom(tiles, 4, 8, 6, 4);
      createCorridor(tiles, 10, 10, 16, 10);
      fillRoom(tiles, 16, 8, 6, 4);
      break;

    case 5:
    case 6:
    case 7:
      // High complexity
      fillRoom(tiles, 8, 14, 8, 6);
      createCorridor(tiles, 12, 14, 12, 10);
      fillRoom(tiles, 6, 8, 12, 6);
      createCorridor(tiles, 6, 11, 3, 11);
      fillRoom(tiles, 1, 9, 4, 4);
      createCorridor(tiles, 18, 11, 21, 11);
      fillRoom(tiles, 19, 9, 4, 4);
      break;

    case 8:
    case 9:
      // Very high complexity
      fillRoom(tiles, 9, 16, 6, 4);
      createCorridor(tiles, 12, 16, 12, 12);
      fillRoom(tiles, 5, 10, 14, 8);
      createCorridor(tiles, 5, 14, 2, 14);
      fillRoom(tiles, 1, 12, 3, 4);
      createCorridor(tiles, 19, 14, 22, 14);
      fillRoom(tiles, 20, 12, 3, 4);
      // Add some chasms
      setTile(tiles, 12, 7, DungeonTileType.CHASM);
      setTile(tiles, 12, 16, DungeonTileType.CHASM);
      break;

    case 10:
      // Chaos chamber - grand and ominous
      fillRoom(tiles, 10, 20, 4, 3);
      createCorridor(tiles, 12, 20, 12, 15);
      // Large boss chamber
      fillRoom(tiles, 6, 8, 12, 12);
      // Four pillars (representing four fiends)
      setTile(tiles, 10, 9, DungeonTileType.PILLAR);
      setTile(tiles, 10, 17, DungeonTileType.PILLAR);
      setTile(tiles, 17, 9, DungeonTileType.PILLAR);
      setTile(tiles, 17, 17, DungeonTileType.PILLAR);
      // Chaos throne
      setTile(tiles, 14, 13, DungeonTileType.THRONE);
      break;
  }

  return tiles;
}

// ========== CITADEL OF TRIALS (Class Upgrade Dungeon) ==========

function createCitadelFloor1(): DungeonTileType[][] {
  const tiles = createEmptyFloor(24, 24);

  // Entry hall from south
  fillRoom(tiles, 10, 20, 4, 4);

  // Main corridor going north
  createCorridor(tiles, 12, 20, 12, 14);

  // Central hub room
  fillRoom(tiles, 8, 10, 8, 6);

  // Four teleporter rooms (corners) - the puzzle of this dungeon
  // Northwest room
  fillRoom(tiles, 2, 2, 5, 5);
  setTile(tiles, 4, 4, DungeonTileType.ALTAR); // Teleporter pad

  // Northeast room
  fillRoom(tiles, 17, 2, 5, 5);
  setTile(tiles, 4, 19, DungeonTileType.ALTAR); // Teleporter pad

  // Southwest room
  fillRoom(tiles, 2, 17, 5, 5);
  setTile(tiles, 19, 4, DungeonTileType.ALTAR); // Teleporter pad

  // Southeast room
  fillRoom(tiles, 17, 17, 5, 5);
  setTile(tiles, 19, 19, DungeonTileType.ALTAR); // Teleporter pad

  // Corridors connecting hub to teleporter rooms
  createCorridor(tiles, 8, 12, 4, 12);
  createCorridor(tiles, 4, 12, 4, 7);

  createCorridor(tiles, 16, 12, 19, 12);
  createCorridor(tiles, 19, 12, 19, 7);

  // Stairs down in hub
  setTile(tiles, 12, 12, DungeonTileType.STAIRS_DOWN);

  return tiles;
}

function createCitadelFloor2(): DungeonTileType[][] {
  const tiles = createEmptyFloor(24, 24);

  // This floor is a maze of corridors with pillars
  // Entry from stairs
  fillRoom(tiles, 10, 2, 4, 4);

  // Long winding corridor
  createCorridor(tiles, 12, 6, 12, 10);
  createCorridor(tiles, 12, 10, 4, 10);
  createCorridor(tiles, 4, 10, 4, 16);
  createCorridor(tiles, 4, 16, 12, 16);
  createCorridor(tiles, 12, 16, 12, 12);

  // Side treasure rooms
  fillRoom(tiles, 18, 8, 4, 4);
  createCorridor(tiles, 12, 10, 18, 10);
  setTile(tiles, 10, 20, DungeonTileType.TREASURE_CLOSED);

  fillRoom(tiles, 18, 16, 4, 4);
  createCorridor(tiles, 12, 18, 18, 18);
  setTile(tiles, 18, 20, DungeonTileType.TREASURE_CLOSED);

  // Pillar room (trials by combat)
  fillRoom(tiles, 6, 18, 8, 4);
  setTile(tiles, 19, 8, DungeonTileType.PILLAR);
  setTile(tiles, 19, 10, DungeonTileType.PILLAR);
  setTile(tiles, 19, 12, DungeonTileType.PILLAR);
  setTile(tiles, 21, 8, DungeonTileType.PILLAR);
  setTile(tiles, 21, 10, DungeonTileType.PILLAR);
  setTile(tiles, 21, 12, DungeonTileType.PILLAR);

  // Stairs down at end
  fillRoom(tiles, 2, 18, 3, 3);
  createCorridor(tiles, 4, 16, 4, 19);
  setTile(tiles, 19, 3, DungeonTileType.STAIRS_DOWN);

  return tiles;
}

function createCitadelFloor3(): DungeonTileType[][] {
  const tiles = createEmptyFloor(24, 24);

  // Final floor - throne room with Rat's Tail
  // Entry from stairs
  fillRoom(tiles, 2, 2, 4, 4);

  // Long approach corridor
  createCorridor(tiles, 4, 6, 4, 12);
  createCorridor(tiles, 4, 12, 12, 12);

  // Grand throne room
  fillRoom(tiles, 8, 8, 12, 12);

  // Decorative pillars
  setTile(tiles, 10, 10, DungeonTileType.PILLAR);
  setTile(tiles, 10, 18, DungeonTileType.PILLAR);
  setTile(tiles, 18, 10, DungeonTileType.PILLAR);
  setTile(tiles, 18, 18, DungeonTileType.PILLAR);

  // Throne at far end
  setTile(tiles, 10, 14, DungeonTileType.THRONE);

  // Rat's Tail treasure chest behind throne
  setTile(tiles, 9, 14, DungeonTileType.TREASURE_CLOSED);

  // Side chambers with additional treasure
  fillRoom(tiles, 2, 14, 4, 4);
  createCorridor(tiles, 6, 16, 8, 16);
  setTile(tiles, 16, 3, DungeonTileType.TREASURE_CLOSED);

  fillRoom(tiles, 18, 14, 4, 4);
  createCorridor(tiles, 16, 16, 18, 16);
  setTile(tiles, 16, 19, DungeonTileType.TREASURE_CLOSED);

  return tiles;
}

// ========== TITAN'S TUNNEL ==========

function createTitansTunnelFloor(): DungeonTileType[][] {
  const tiles = createEmptyFloor(16, 24);

  // West entrance
  fillRoom(tiles, 6, 2, 4, 4);

  // Long tunnel through mountain
  createCorridor(tiles, 8, 6, 8, 10);

  // Titan's chamber (wide room in middle)
  fillRoom(tiles, 4, 10, 8, 6);
  setTile(tiles, 13, 8, DungeonTileType.PILLAR); // Titan sits here

  // Continue tunnel
  createCorridor(tiles, 8, 16, 8, 18);

  // East exit
  fillRoom(tiles, 6, 18, 4, 4);

  return tiles;
}

// ========== WATERFALL CAVE ==========

function createWaterfallCaveFloor(): DungeonTileType[][] {
  const tiles = createEmptyFloor(20, 20);

  // Entry through waterfall - larger entry area
  fillRoom(tiles, 8, 1, 4, 4);

  // Water decorations at the sides of entry (not blocking it)
  setTile(tiles, 1, 7, DungeonTileType.WATER);
  setTile(tiles, 2, 7, DungeonTileType.WATER);
  setTile(tiles, 1, 12, DungeonTileType.WATER);
  setTile(tiles, 2, 12, DungeonTileType.WATER);

  // Main cavern
  createCorridor(tiles, 10, 5, 10, 8);
  fillRoom(tiles, 4, 8, 12, 8);

  // Waterfall streams through the cave (on sides, not blocking path)
  for (let y = 8; y < 16; y++) {
    setTile(tiles, y, 5, DungeonTileType.WATER);
    setTile(tiles, y, 15, DungeonTileType.WATER);
  }

  // Robot chamber at back
  fillRoom(tiles, 8, 14, 4, 4);
  createCorridor(tiles, 10, 12, 10, 14);

  // Treasure rooms
  fillRoom(tiles, 2, 10, 3, 3);
  createCorridor(tiles, 4, 11, 5, 11);
  setTile(tiles, 11, 3, DungeonTileType.TREASURE_CLOSED);

  fillRoom(tiles, 15, 10, 3, 3);
  createCorridor(tiles, 13, 11, 15, 11);
  setTile(tiles, 11, 16, DungeonTileType.TREASURE_CLOSED);

  return tiles;
}

// ========== DRAGON CAVES (CARDIA) ==========

function createDragonCavesFloor(): DungeonTileType[][] {
  const tiles = createEmptyFloor(24, 24);

  // Entry cavern
  fillRoom(tiles, 10, 1, 4, 4);

  // Winding path through volcanic terrain
  createCorridor(tiles, 12, 5, 12, 8);
  createCorridor(tiles, 12, 8, 6, 8);
  createCorridor(tiles, 6, 8, 6, 14);

  // Lava pools scattered around
  setTile(tiles, 10, 4, DungeonTileType.LAVA);
  setTile(tiles, 10, 8, DungeonTileType.LAVA);
  setTile(tiles, 12, 16, DungeonTileType.LAVA);
  setTile(tiles, 12, 18, DungeonTileType.LAVA);

  // Dragon nesting areas (side caves)
  fillRoom(tiles, 2, 6, 4, 4);
  createCorridor(tiles, 6, 8, 4, 8);

  fillRoom(tiles, 16, 6, 4, 4);
  createCorridor(tiles, 12, 8, 16, 8);

  // Main corridor continues
  createCorridor(tiles, 6, 14, 12, 14);

  // Bahamut's throne room
  fillRoom(tiles, 6, 16, 12, 6);

  // Lava moat around throne
  for (let x = 8; x < 16; x++) {
    setTile(tiles, 17, x, DungeonTileType.LAVA);
  }

  // Bahamut's throne
  setTile(tiles, 19, 12, DungeonTileType.THRONE);

  // Treasure hoards
  setTile(tiles, 20, 8, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 20, 16, DungeonTileType.TREASURE_CLOSED);

  return tiles;
}

// ========== TREASURE CAVE (SMALL) ==========

function createTreasureCaveFloor(): DungeonTileType[][] {
  const tiles = createEmptyFloor(16, 16);

  // Entry
  fillRoom(tiles, 6, 1, 4, 3);

  // Main chamber filled with chests
  createCorridor(tiles, 8, 4, 8, 6);
  fillRoom(tiles, 2, 6, 12, 8);

  // Treasure everywhere!
  setTile(tiles, 8, 4, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 8, 6, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 8, 10, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 8, 12, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 10, 3, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 10, 13, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 12, 6, DungeonTileType.TREASURE_CLOSED);
  setTile(tiles, 12, 10, DungeonTileType.TREASURE_CLOSED);

  // Pillars
  setTile(tiles, 9, 5, DungeonTileType.PILLAR);
  setTile(tiles, 9, 11, DungeonTileType.PILLAR);
  setTile(tiles, 11, 5, DungeonTileType.PILLAR);
  setTile(tiles, 11, 11, DungeonTileType.PILLAR);

  return tiles;
}

// ========== DUNGEON DEFINITIONS ==========

export const DUNGEONS: Record<string, Dungeon> = {
  CITADEL_OF_TRIALS: {
    id: 'CITADEL_OF_TRIALS',
    name: 'Citadel of Trials',
    description: 'A test of courage for those seeking to prove their worth',
    entryFloor: 1,
    entryX: 12,
    entryY: 22,
    floors: [
      {
        width: 24,
        height: 24,
        tiles: createCitadelFloor1(),
        stairs: [{ x: 12, y: 12, direction: 'down', targetFloor: 2, targetX: 12, targetY: 3 }],
        encounterRate: 18,
        encounterZone: 'CITADEL',
        ambientColor: '#333344',
      },
      {
        width: 24,
        height: 24,
        tiles: createCitadelFloor2(),
        stairs: [
          { x: 12, y: 3, direction: 'up', targetFloor: 1, targetX: 12, targetY: 12 },
          { x: 3, y: 19, direction: 'down', targetFloor: 3, targetX: 3, targetY: 3 },
        ],
        encounterRate: 20,
        encounterZone: 'CITADEL',
        ambientColor: '#333344',
      },
      {
        width: 24,
        height: 24,
        tiles: createCitadelFloor3(),
        stairs: [{ x: 3, y: 3, direction: 'up', targetFloor: 2, targetX: 3, targetY: 19 }],
        npcs: [
          {
            x: 14,
            y: 11,
            id: 'OLD_MAN_CITADEL',
            name: 'Old Man',
            dialogue: [
              'You have proven your courage...',
              'Take the Rat\'s Tail as proof of your trial.',
              'Present it to Bahamut, King of Dragons.',
            ],
          },
        ],
        encounterRate: 10,
        encounterZone: 'CITADEL',
        ambientColor: '#444455',
      },
    ],
  },

  TITANS_TUNNEL: {
    id: 'TITANS_TUNNEL',
    name: "Titan's Tunnel",
    description: 'A passage through the mountains, blocked by a hungry titan',
    entryFloor: 1,
    entryX: 8,
    entryY: 3,
    requiredItem: 'RUBY',
    floors: [
      {
        width: 16,
        height: 24,
        tiles: createTitansTunnelFloor(),
        stairs: [],
        npcs: [
          {
            x: 8,
            y: 13,
            id: 'TITAN',
            name: 'Titan',
            dialogue: [
              'HUNGRY...',
              'RUBY... GIVE TITAN RUBY...',
              'TITAN LET YOU PASS FOR RUBY.',
            ],
          },
        ],
        encounterRate: 8,
        encounterZone: 'EARTH_CAVE',
        ambientColor: '#443322',
      },
    ],
  },

  WATERFALL_CAVE: {
    id: 'WATERFALL_CAVE',
    name: 'Waterfall Cave',
    description: 'A hidden cave behind a waterfall',
    entryFloor: 1,
    entryX: 10,
    entryY: 2,
    floors: [
      {
        width: 20,
        height: 20,
        tiles: createWaterfallCaveFloor(),
        stairs: [],
        npcs: [
          {
            x: 10,
            y: 16,
            id: 'ROBOT',
            name: 'Robot',
            dialogue: [
              'BEEP BOOP...',
              'I AM A CREATION OF THE ANCIENTS.',
              'I WILL AID YOU IN YOUR QUEST.',
              'USE THE CUBE TO REACH THE FLOATING CASTLE.',
            ],
          },
        ],
        encounterRate: 12,
        encounterZone: 'WATERFALL',
        ambientColor: '#224466',
      },
    ],
  },

  DRAGON_CAVES: {
    id: 'DRAGON_CAVES',
    name: 'Dragon Caves',
    description: 'The lair of Bahamut, King of Dragons',
    entryFloor: 1,
    entryX: 12,
    entryY: 2,
    floors: [
      {
        width: 24,
        height: 24,
        tiles: createDragonCavesFloor(),
        stairs: [],
        npcs: [
          {
            x: 12,
            y: 19,
            id: 'BAHAMUT',
            name: 'Bahamut',
            dialogue: [
              'I am Bahamut, King of Dragons.',
              'Have you proven your worth at the Citadel of Trials?',
              'Show me the Rat\'s Tail...',
              'Your courage is genuine. I shall grant you power!',
            ],
          },
        ],
        encounterRate: 25,
        encounterZone: 'CARDIA',
        ambientColor: '#442222',
      },
    ],
  },

  TREASURE_CAVE: {
    id: 'TREASURE_CAVE',
    name: 'Treasure Cave',
    description: 'A hidden cave filled with ancient treasures',
    entryFloor: 1,
    entryX: 8,
    entryY: 2,
    floors: [
      {
        width: 16,
        height: 16,
        tiles: createTreasureCaveFloor(),
        stairs: [],
        encounterRate: 15,
        encounterZone: 'CARDIA',
        ambientColor: '#554433',
      },
    ],
  },

  TEMPLE_OF_FIENDS: {
    id: 'TEMPLE_OF_FIENDS',
    name: 'Temple of Fiends',
    description: 'An ancient temple corrupted by darkness',
    entryFloor: 1,
    entryX: 10,
    entryY: 2,
    bossFloor: 3,
    bossId: 43, // Garland (initial)
    floors: [
      {
        width: 20,
        height: 20,
        tiles: createTempleOfFiendsFloor1(),
        stairs: [
          { x: 10, y: 15, direction: 'down', targetFloor: 2, targetX: 10, targetY: 17 },
        ],
        encounterRate: 12,
        encounterZone: 'TEMPLE_FIENDS',
        ambientColor: '#332222',
      },
      {
        width: 20,
        height: 20,
        tiles: createTempleOfFiendsFloor2(),
        stairs: [
          { x: 10, y: 17, direction: 'up', targetFloor: 1, targetX: 10, targetY: 15 },
          { x: 14, y: 4, direction: 'down', targetFloor: 3, targetX: 10, targetY: 17 },
        ],
        encounterRate: 15,
        encounterZone: 'TEMPLE_FIENDS',
        ambientColor: '#332222',
      },
      {
        width: 20,
        height: 20,
        tiles: createTempleOfFiendsFloor3(),
        stairs: [
          { x: 10, y: 17, direction: 'up', targetFloor: 2, targetX: 14, targetY: 4 },
        ],
        npcs: [
          {
            x: 10,
            y: 10,
            id: 'GARLAND',
            name: 'Garland',
            dialogue: ['I, Garland, will knock you all down!'],
            isBoss: true,
            enemyId: 43,
          },
        ],
        encounterRate: 0, // No random encounters on boss floor
        encounterZone: 'TEMPLE_FIENDS',
        ambientColor: '#442222',
      },
    ],
  },

  MARSH_CAVE: {
    id: 'MARSH_CAVE',
    name: 'Marsh Cave',
    description: 'A treacherous cave hidden in the swamps',
    entryFloor: 1,
    entryX: 10,
    entryY: 18,
    floors: [
      {
        width: 20,
        height: 20,
        tiles: createMarshCaveFloor1(),
        stairs: [
          { x: 10, y: 10, direction: 'down', targetFloor: 2, targetX: 10, targetY: 2 },
        ],
        encounterRate: 15,
        encounterZone: 'MARSH',
        ambientColor: '#224422',
      },
      {
        width: 20,
        height: 20,
        tiles: createMarshCaveFloor2(),
        stairs: [
          { x: 10, y: 2, direction: 'up', targetFloor: 1, targetX: 10, targetY: 10 },
          { x: 10, y: 17, direction: 'down', targetFloor: 3, targetX: 10, targetY: 17 },
        ],
        encounterRate: 18,
        encounterZone: 'MARSH',
        ambientColor: '#224422',
      },
      {
        width: 20,
        height: 20,
        tiles: createMarshCaveFloor3(),
        stairs: [
          { x: 10, y: 17, direction: 'up', targetFloor: 2, targetX: 10, targetY: 17 },
        ],
        encounterRate: 20,
        encounterZone: 'MARSH',
        ambientColor: '#224422',
      },
    ],
  },

  EARTH_CAVE: {
    id: 'EARTH_CAVE',
    name: 'Earth Cave',
    description: "The lair of Lich, the Earth Fiend",
    entryFloor: 1,
    entryX: 11,
    entryY: 19,
    bossFloor: 5,
    bossId: 39, // Lich
    requiredItem: 'STAR_RUBY',
    floors: [
      {
        width: 22,
        height: 22,
        tiles: createEarthCaveFloor1(),
        stairs: [
          { x: 11, y: 8, direction: 'down', targetFloor: 2, targetX: 11, targetY: 2 },
        ],
        encounterRate: 15,
        encounterZone: 'EARTH_CAVE',
        ambientColor: '#443322',
      },
      {
        width: 22,
        height: 22,
        tiles: createEarthCaveFloor2(),
        stairs: [
          { x: 11, y: 2, direction: 'up', targetFloor: 1, targetX: 11, targetY: 8 },
          { x: 11, y: 20, direction: 'down', targetFloor: 3, targetX: 11, targetY: 20 },
        ],
        encounterRate: 18,
        encounterZone: 'EARTH_CAVE',
        ambientColor: '#443322',
      },
      {
        width: 22,
        height: 22,
        tiles: createEarthCaveFloor3(),
        stairs: [
          { x: 11, y: 20, direction: 'up', targetFloor: 2, targetX: 11, targetY: 20 },
          { x: 13, y: 6, direction: 'down', targetFloor: 4, targetX: 11, targetY: 2 },
        ],
        encounterRate: 20,
        encounterZone: 'EARTH_CAVE',
        ambientColor: '#443322',
      },
      {
        width: 22,
        height: 22,
        tiles: createEarthCaveFloor4(),
        stairs: [
          { x: 11, y: 2, direction: 'up', targetFloor: 3, targetX: 13, targetY: 6 },
          { x: 11, y: 20, direction: 'down', targetFloor: 5, targetX: 11, targetY: 20 },
        ],
        encounterRate: 22,
        encounterZone: 'EARTH_CAVE',
        ambientColor: '#443322',
      },
      {
        width: 22,
        height: 22,
        tiles: createEarthCaveFloor5(),
        stairs: [
          { x: 11, y: 20, direction: 'up', targetFloor: 4, targetX: 11, targetY: 20 },
        ],
        npcs: [
          {
            x: 11,
            y: 13,
            id: 'LICH',
            name: 'Lich',
            dialogue: ['The Earth rots...'],
            isBoss: true,
            enemyId: 39,
          },
        ],
        encounterRate: 0,
        encounterZone: 'EARTH_CAVE',
        ambientColor: '#553322',
      },
    ],
  },

  GURGU_VOLCANO: {
    id: 'GURGU_VOLCANO',
    name: 'Gurgu Volcano',
    description: 'A fiery volcano where Kary resides',
    entryFloor: 1,
    entryX: 11,
    entryY: 19,
    bossFloor: 5,
    bossId: 40, // Kary
    requiredItem: 'LEVISTONE',
    floors: [
      {
        width: 22,
        height: 22,
        tiles: createVolcanoFloor1(),
        stairs: [
          { x: 11, y: 10, direction: 'down', targetFloor: 2, targetX: 11, targetY: 2 },
        ],
        encounterRate: 18,
        encounterZone: 'VOLCANO',
        ambientColor: '#554422',
      },
      {
        width: 22,
        height: 22,
        tiles: createVolcanoFloor2(),
        stairs: [
          { x: 11, y: 2, direction: 'up', targetFloor: 1, targetX: 11, targetY: 10 },
          { x: 11, y: 20, direction: 'down', targetFloor: 3, targetX: 11, targetY: 20 },
        ],
        encounterRate: 20,
        encounterZone: 'VOLCANO',
        ambientColor: '#554422',
      },
      {
        width: 22,
        height: 22,
        tiles: createVolcanoFloor3(),
        stairs: [
          { x: 11, y: 20, direction: 'up', targetFloor: 2, targetX: 11, targetY: 20 },
          { x: 11, y: 12, direction: 'down', targetFloor: 4, targetX: 11, targetY: 2 },
        ],
        encounterRate: 22,
        encounterZone: 'VOLCANO',
        ambientColor: '#664422',
      },
      {
        width: 22,
        height: 22,
        tiles: createVolcanoFloor4(),
        stairs: [
          { x: 11, y: 2, direction: 'up', targetFloor: 3, targetX: 11, targetY: 12 },
          { x: 11, y: 20, direction: 'down', targetFloor: 5, targetX: 11, targetY: 20 },
        ],
        encounterRate: 24,
        encounterZone: 'VOLCANO',
        ambientColor: '#664422',
      },
      {
        width: 22,
        height: 22,
        tiles: createVolcanoFloor5(),
        stairs: [
          { x: 11, y: 20, direction: 'up', targetFloor: 4, targetX: 11, targetY: 20 },
        ],
        npcs: [
          {
            x: 11,
            y: 13,
            id: 'KARY',
            name: 'Kary',
            dialogue: ['Feel the flames of destruction!'],
            isBoss: true,
            enemyId: 40,
          },
        ],
        encounterRate: 0,
        encounterZone: 'VOLCANO',
        ambientColor: '#774422',
      },
    ],
  },

  ICE_CAVE: {
    id: 'ICE_CAVE',
    name: 'Ice Cave',
    description: 'A frozen cavern',
    entryFloor: 1,
    entryX: 10,
    entryY: 18,
    floors: [
      {
        width: 20,
        height: 20,
        tiles: createIceCaveFloor1(),
        stairs: [
          { x: 10, y: 10, direction: 'down', targetFloor: 2, targetX: 10, targetY: 2 },
        ],
        encounterRate: 15,
        encounterZone: 'ICE_CAVE',
        ambientColor: '#ccddff',
      },
      {
        width: 20,
        height: 20,
        tiles: createIceCaveFloor2(),
        stairs: [
          { x: 10, y: 2, direction: 'up', targetFloor: 1, targetX: 10, targetY: 10 },
          { x: 10, y: 19, direction: 'down', targetFloor: 3, targetX: 10, targetY: 19 },
        ],
        encounterRate: 18,
        encounterZone: 'ICE_CAVE',
        ambientColor: '#ccddff',
      },
      {
        width: 20,
        height: 20,
        tiles: createIceCaveFloor3(),
        stairs: [
          { x: 10, y: 19, direction: 'up', targetFloor: 2, targetX: 10, targetY: 19 },
        ],
        encounterRate: 20,
        encounterZone: 'ICE_CAVE',
        ambientColor: '#ccddff',
      },
    ],
  },

  SEA_SHRINE: {
    id: 'SEA_SHRINE',
    name: 'Sea Shrine',
    description: 'An underwater temple where Kraken dwells',
    entryFloor: 1,
    entryX: 11,
    entryY: 19,
    bossFloor: 5,
    bossId: 41, // Kraken
    requiredItem: 'OXYALE',
    floors: [
      {
        width: 22,
        height: 22,
        tiles: createSeaShrineFloor1(),
        stairs: [
          { x: 11, y: 6, direction: 'down', targetFloor: 2, targetX: 11, targetY: 2 },
        ],
        encounterRate: 20,
        encounterZone: 'SEA_SHRINE',
        ambientColor: '#224466',
      },
      {
        width: 22,
        height: 22,
        tiles: createSeaShrineFloor2(),
        stairs: [
          { x: 11, y: 2, direction: 'up', targetFloor: 1, targetX: 11, targetY: 6 },
          { x: 11, y: 20, direction: 'down', targetFloor: 3, targetX: 11, targetY: 20 },
        ],
        encounterRate: 22,
        encounterZone: 'SEA_SHRINE',
        ambientColor: '#224466',
      },
      {
        width: 22,
        height: 22,
        tiles: createSeaShrineFloor3(),
        stairs: [
          { x: 11, y: 20, direction: 'up', targetFloor: 2, targetX: 11, targetY: 20 },
          { x: 10, y: 10, direction: 'down', targetFloor: 4, targetX: 11, targetY: 2 },
        ],
        encounterRate: 24,
        encounterZone: 'SEA_SHRINE',
        ambientColor: '#224466',
      },
      {
        width: 22,
        height: 22,
        tiles: createSeaShrineFloor4(),
        stairs: [
          { x: 11, y: 2, direction: 'up', targetFloor: 3, targetX: 10, targetY: 10 },
          { x: 11, y: 20, direction: 'down', targetFloor: 5, targetX: 11, targetY: 20 },
        ],
        encounterRate: 26,
        encounterZone: 'SEA_SHRINE',
        ambientColor: '#224466',
      },
      {
        width: 22,
        height: 22,
        tiles: createSeaShrineFloor5(),
        stairs: [
          { x: 11, y: 20, direction: 'up', targetFloor: 4, targetX: 11, targetY: 20 },
        ],
        npcs: [
          {
            x: 11,
            y: 13,
            id: 'KRAKEN',
            name: 'Kraken',
            dialogue: ['The seas will swallow you!'],
            isBoss: true,
            enemyId: 41,
          },
        ],
        encounterRate: 0,
        encounterZone: 'SEA_SHRINE',
        ambientColor: '#224477',
      },
    ],
  },

  MIRAGE_TOWER: {
    id: 'MIRAGE_TOWER',
    name: 'Mirage Tower',
    description: 'A mysterious tower that appears in the desert',
    entryFloor: 1,
    entryX: 10,
    entryY: 18,
    requiredItem: 'CHIME',
    floors: [
      {
        width: 20,
        height: 20,
        tiles: createMirageTowerFloor1(),
        stairs: [
          { x: 10, y: 10, direction: 'up', targetFloor: 2, targetX: 10, targetY: 2 },
        ],
        encounterRate: 18,
        encounterZone: 'MIRAGE_TOWER',
        ambientColor: '#ffddaa',
      },
      {
        width: 20,
        height: 20,
        tiles: createMirageTowerFloor2(),
        stairs: [
          { x: 10, y: 2, direction: 'down', targetFloor: 1, targetX: 10, targetY: 10 },
          { x: 10, y: 18, direction: 'up', targetFloor: 3, targetX: 10, targetY: 19 },
        ],
        encounterRate: 20,
        encounterZone: 'MIRAGE_TOWER',
        ambientColor: '#ffddaa',
      },
      {
        width: 20,
        height: 20,
        tiles: createMirageTowerFloor3(),
        stairs: [
          { x: 10, y: 19, direction: 'down', targetFloor: 2, targetX: 10, targetY: 18 },
          { x: 10, y: 10, direction: 'up', targetFloor: 0, targetX: 11, targetY: 19 }, // Warp to Sky Castle
        ],
        encounterRate: 22,
        encounterZone: 'MIRAGE_TOWER',
        ambientColor: '#ffddaa',
      },
    ],
  },

  SKY_CASTLE: {
    id: 'SKY_CASTLE',
    name: 'Sky Castle',
    description: "The flying fortress of Tiamat",
    entryFloor: 1,
    entryX: 11,
    entryY: 19,
    bossFloor: 5,
    bossId: 42, // Tiamat
    floors: [
      {
        width: 22,
        height: 22,
        tiles: createSkyCastleFloor1(),
        stairs: [
          { x: 11, y: 10, direction: 'up', targetFloor: 2, targetX: 11, targetY: 2 },
        ],
        encounterRate: 20,
        encounterZone: 'SKY_CASTLE',
        ambientColor: '#ddddff',
      },
      {
        width: 22,
        height: 22,
        tiles: createSkyCastleFloor2(),
        stairs: [
          { x: 11, y: 2, direction: 'down', targetFloor: 1, targetX: 11, targetY: 10 },
          { x: 11, y: 20, direction: 'up', targetFloor: 3, targetX: 11, targetY: 20 },
        ],
        encounterRate: 22,
        encounterZone: 'SKY_CASTLE',
        ambientColor: '#ddddff',
      },
      {
        width: 22,
        height: 22,
        tiles: createSkyCastleFloor3(),
        stairs: [
          { x: 11, y: 20, direction: 'down', targetFloor: 2, targetX: 11, targetY: 20 },
          { x: 10, y: 11, direction: 'up', targetFloor: 4, targetX: 11, targetY: 2 },
        ],
        encounterRate: 24,
        encounterZone: 'SKY_CASTLE',
        ambientColor: '#ddddff',
      },
      {
        width: 22,
        height: 22,
        tiles: createSkyCastleFloor4(),
        stairs: [
          { x: 11, y: 2, direction: 'down', targetFloor: 3, targetX: 10, targetY: 11 },
          { x: 11, y: 20, direction: 'up', targetFloor: 5, targetX: 11, targetY: 20 },
        ],
        encounterRate: 26,
        encounterZone: 'SKY_CASTLE',
        ambientColor: '#ddddff',
      },
      {
        width: 22,
        height: 22,
        tiles: createSkyCastleFloor5(),
        stairs: [
          { x: 11, y: 20, direction: 'down', targetFloor: 4, targetX: 11, targetY: 20 },
        ],
        npcs: [
          {
            x: 11,
            y: 13,
            id: 'TIAMAT',
            name: 'Tiamat',
            dialogue: ['The winds of chaos shall consume you!'],
            isBoss: true,
            enemyId: 42,
          },
        ],
        encounterRate: 0,
        encounterZone: 'SKY_CASTLE',
        ambientColor: '#eeeeff',
      },
    ],
  },

  TEMPLE_FIENDS_REVISITED: {
    id: 'TEMPLE_FIENDS_REVISITED',
    name: 'Temple of Fiends (Past)',
    description: '2000 years in the past - face the Four Fiends and Chaos',
    entryFloor: 1,
    entryX: 12,
    entryY: 21,
    bossFloor: 10,
    bossId: 43, // Chaos (final form)
    requiredItem: 'ALL_CRYSTALS',
    floors: Array.from({ length: 10 }, (_, i) => ({
      width: 24,
      height: 24,
      tiles: createTemplePastFloor(i + 1),
      stairs:
        i === 0
          ? // Floor 1: only stairs down to floor 2
            [{ x: 12, y: 8, direction: 'down', targetFloor: 2, targetX: 12, targetY: 21 }]
          : i === 9
            ? // Floor 10: only stairs up to floor 9
              [{ x: 12, y: 21, direction: 'up', targetFloor: 9, targetX: 12, targetY: 8 }]
            : // Floors 2-9: stairs up and down
              [
                { x: 12, y: 21, direction: 'up', targetFloor: i, targetX: 12, targetY: 8 },
                { x: 12, y: 8, direction: 'down', targetFloor: i + 2, targetX: 12, targetY: 21 },
              ],
      npcs:
        i === 9
          ? [
              {
                x: 12,
                y: 14,
                id: 'CHAOS',
                name: 'Chaos',
                dialogue: [
                  'I am Chaos...',
                  'The beginning and the end...',
                  'All shall return to nothing!',
                ],
                isBoss: true,
                enemyId: 43,
              },
            ]
          : undefined,
      encounterRate: i === 9 ? 0 : 22 + i * 2,
      encounterZone: 'TEMPLE_PAST',
      ambientColor: '#220033',
    })),
  },
};

// Helper to get dungeon by ID
export function getDungeon(dungeonId: string): Dungeon | null {
  return DUNGEONS[dungeonId] ?? null;
}

// Helper to check if tile is walkable
export function isDungeonTileWalkable(tile: DungeonTileType): boolean {
  switch (tile) {
    case DungeonTileType.FLOOR:
    case DungeonTileType.DOOR:
    case DungeonTileType.STAIRS_UP:
    case DungeonTileType.STAIRS_DOWN:
    case DungeonTileType.ALTAR:
    case DungeonTileType.THRONE:
    case DungeonTileType.TREASURE_CLOSED:
    case DungeonTileType.TREASURE_OPEN:
      return true;
    default:
      return false;
  }
}

// Get tile color for rendering (fallback)
export function getDungeonTileColor(tile: DungeonTileType): string {
  switch (tile) {
    case DungeonTileType.FLOOR:
      return '#666666';
    case DungeonTileType.WALL:
      return '#333333';
    case DungeonTileType.DOOR:
      return '#884422';
    case DungeonTileType.STAIRS_UP:
      return '#8888ff';
    case DungeonTileType.STAIRS_DOWN:
      return '#ff8888';
    case DungeonTileType.PILLAR:
      return '#444444';
    case DungeonTileType.WATER:
      return '#2244ff';
    case DungeonTileType.LAVA:
      return '#ff4400';
    case DungeonTileType.CHASM:
      return '#000000';
    case DungeonTileType.ALTAR:
      return '#ffcc00';
    case DungeonTileType.THRONE:
      return '#cc0000';
    case DungeonTileType.TREASURE_CLOSED:
      return '#ffcc00';
    case DungeonTileType.TREASURE_OPEN:
      return '#886644';
    default:
      return '#ff00ff';
  }
}
